import { useMemo } from 'react'
import { Pencil } from 'lucide-react'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'

import Button from '@/elements/Button'
import ExternalLink from '@/elements/ExternalLink'
import Flex from '@/elements/Flex'
import Pill from '@/elements/Pill'
import Text from '@/elements/Text'
import { defaultColumnOptions, type TableAny } from '@/utils/tables'
import type { SponsorshipResponse } from '@/api/types/Sponsorship'

export interface IndividualSponsorshipTableType {
  id: SponsorshipResponse['id']
  name: SponsorshipResponse['name']
  url: SponsorshipResponse['url']
  beginningDate: SponsorshipResponse['beginningDate']
  endDate: SponsorshipResponse['endDate']
  status: SponsorshipResponse['status']
}

const columnHelper = createColumnHelper<IndividualSponsorshipTableType>()

export function useIndividualSponsorshipTable(
  sponsorship: SponsorshipResponse[],
  onEdit: (sponsorship: SponsorshipResponse) => void
) {
  const data: IndividualSponsorshipTableType[] = useMemo(() => (
    sponsorship ? sponsorship.map(row => ({
      id: row?.id,
      name: row?.name || '',
      url: row?.url || '',
      beginningDate: row?.beginningDate || '',
      endDate: row?.endDate || '',
      status: row?.status || '',
    })) : []
  ), [sponsorship])

  const columns: ColumnDef<IndividualSponsorshipTableType, TableAny>[] = [
    columnHelper.accessor('name', {
      ...defaultColumnOptions,
      header: 'Brand',
      enableSorting: false,
      cell: info => (
        <Flex inline align='center' gap={2} className='user-cell'>
          <Text variant='textblock'>{info.getValue() as string}</Text>
        </Flex>
      )
    }),
    columnHelper.accessor('url', {
      ...defaultColumnOptions,
      header: 'URL',
      enableSorting: false,
      meta: { width: 200 },
      cell: info => {
        const url = info.getValue()
        return (
          <ExternalLink
            to={url}
            hasIcon={false}
            variant='black'
            size='small'
            maxLines={1}
          >
            {url}
          </ExternalLink>
        )
      }
    }),
    columnHelper.accessor('beginningDate', {
      ...defaultColumnOptions,
      header: 'Starting Date',
      enableSorting: false,
      cell: info => {
        const date = new Date(info.getValue())
        return date.toLocaleDateString()
      }
    }),
    columnHelper.accessor('endDate', {
      ...defaultColumnOptions,
      header: 'End Date',
      enableSorting: false,
      cell: info => {
        const date = new Date(info.getValue())
        return date.toLocaleDateString()
      }
    }),
    columnHelper.accessor('status', {
      ...defaultColumnOptions,
      header: 'Status',
      enableSorting: false,
      cell: info => {
        const status = info.getValue()
        return (
          <Pill variant='status' status={status}>{status}</Pill>
        )
      }
    }),
    columnHelper.display({
      id: 'actions',
      ...defaultColumnOptions(),
      header: 'Actions',
      meta: { width: 100 },
      enableSorting: false,
      cell: info => {
        return (
          <Flex justify='center' align='center' className='actions-cell'>
            <Button
              small
              variant='iconBtn'
              styles={{ border: 'none' }}
              onClick={() => onEdit(sponsorship.find(s => s.id === info.row.original.id)!)}
            >
              <Pencil size={18} />
            </Button>
          </Flex>
        )
      }
    }),
  ]

  return { data, columns }
}
